import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {
    getIsAnyCreatingNewState,
    getIsShowingWithoutId
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';
import {setBucketLink} from '../reducers/bucket-link';

const messages = defineMessages({
    defaultProjectTitle: {
        id: 'gui.gui.defaultProjectTitle',
        description: 'Default title for project',
        defaultMessage: 'Scratch Project'
    }
});
//OMiLAB Added default value for Bucket link.
const defaultBucketLink = 'https://bucket.omilab.org/scratch-adoscripts/';

/* Higher Order Component to get and set the project title
 * @param {React.Component} WrappedComponent component to receive project title related props
 * @returns {React.Component} component with project loading behavior
 */
const TitledHOC = function (WrappedComponent) {
    class TitledComponent extends React.Component {
        componentDidMount () {
            this.handleReceivedProjectTitle(this.props.projectTitle);
            this.handleReceivedBucketLink(this.props.bucketLink);
        }
        componentDidUpdate (prevProps) {
            if (this.props.projectTitle !== prevProps.projectTitle) {
                this.handleReceivedProjectTitle(this.props.projectTitle);
            }
            //OMiLAB Extended this to also handle the Bucket link.
            if (this.props.bucketLink !== prevProps.bucketLink) {
                this.handleReceivedBucketLink(this.props.bucketLink);
            }
            // if project is a new default project, and has loaded,
            if (this.props.isShowingWithoutId && prevProps.isAnyCreatingNewState) {
                // reset title to default
                const defaultProjectTitle = this.handleReceivedProjectTitle();
                this.props.onUpdateProjectTitle(defaultProjectTitle);
                //OMiLAB Extended this to also handle the Bucket link.
                const defaultBucketLink = this.handleReceivedBucketLink();
                this.props.onUpdateBucketLink(defaultBucketLink);
            }
            // if the projectTitle hasn't changed, but the reduxProjectTitle
            // HAS changed, we need to report that change to the projectTitle's owner
            if (this.props.reduxProjectTitle !== prevProps.reduxProjectTitle &&
                this.props.reduxProjectTitle !== this.props.projectTitle) {
                this.props.onUpdateProjectTitle(this.props.reduxProjectTitle);
            }if (this.props.reduxBucketLink !== prevProps.reduxBucketLink &&
                this.props.reduxBucketLink !== this.props.bucketLink) {
                this.props.onUpdateBucketLink(this.props.reduxBucketLink);
            }
            //OMiLAB Extended this to also handle the Bucket link.
        }
        handleReceivedProjectTitle (requestedTitle) {
            let newTitle = requestedTitle;
            if (newTitle === null || typeof newTitle === 'undefined') {
                newTitle = this.props.intl.formatMessage(messages.defaultProjectTitle);
            }
            this.props.onChangedProjectTitle(newTitle);
            return newTitle;
        }
        //OMiLAB Added function.
        handleReceivedBucketLink (requestedLink) {
            let newLink = requestedLink;
            if (newLink === null || typeof newLink === 'undefined') {
                newLink = defaultBucketLink;
            }
            this.props.onChangedBucketLink(newLink);
            return newLink;
        }
        render () {
            const {
                /* eslint-disable no-unused-vars */
                intl,
                isAnyCreatingNewState,
                isShowingWithoutId,
                onChangedProjectTitle,
                onChangedBucketLink, //OMiLAB Added for Bucket link.
                // for children, we replace onUpdateProjectTitle with our own
                onUpdateProjectTitle,
                onUpdateBucketLink, //OMiLAB Added for Bucket link.
                // we don't pass projectTitle prop to children -- they must use
                // redux value
                projectTitle,
                reduxProjectTitle,
                bucketLink, //OMiLAB Added for Bucket link.
                reduxBucketLink, //OMiLAB Added for Bucket link.
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;
            return (
                <WrappedComponent
                    {...componentProps}
                />
            );
        }
    }

    TitledComponent.propTypes = {
        intl: intlShape,
        isAnyCreatingNewState: PropTypes.bool,
        isShowingWithoutId: PropTypes.bool,
        onChangedProjectTitle: PropTypes.func,
        onUpdateProjectTitle: PropTypes.func,
        projectTitle: PropTypes.string,
        reduxProjectTitle: PropTypes.string,
        bucketLink: PropTypes.string, //OMiLAB Added for Bucket link.
        reduxBucketLink: PropTypes.string //OMiLAB Added for Bucket link.
    };

    TitledComponent.defaultProps = {
        onUpdateProjectTitle: () => {},
        onUpdateBucketLink: () => {} //OMiLAB Added for Bucket link.
    };

    const mapStateToProps = state => {
        const loadingState = state.scratchGui.projectState.loadingState;
        return {
            isAnyCreatingNewState: getIsAnyCreatingNewState(loadingState),
            isShowingWithoutId: getIsShowingWithoutId(loadingState),
            reduxProjectTitle: state.scratchGui.projectTitle,
            reduxBucketLink: state.scratchGui.bucketLink //OMiLAB Added for Bucket link.
        };
    };

    const mapDispatchToProps = dispatch => ({
        onChangedProjectTitle: title => dispatch(setProjectTitle(title)),
        onChangedBucketLink: link => dispatch(setBucketLink(link)) //OMiLAB Added dispatch to Bucket link.
    });

    return injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps
    )(TitledComponent));
};

export {
    TitledHOC as default
};
